// MultiSelectFilter — vícevýběrový filtr v designu aplikace (nahrazuje nativní <select>).
//
// Prázdný výběr = "Vše" (žádný filtr). Vybrané hodnoty se sčítají (OR).
// Použití:
//   <MultiSelectFilter
//     options={[{value:"a",label:"A"},...]}
//     selected={["a"]}
//     onChange={(arr) => setFilter(arr)}
//     placeholder="Vše"
//   />

import React, { useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check } from "lucide-react";

export default function MultiSelectFilter({ options = [], selected = [], onChange, placeholder = "Vše", title }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const active = selected.length > 0;

  const label = !active
    ? placeholder
    : selected.length === 1
      ? (options.find((o) => o.value === selected[0])?.label || selected[0])
      : `${selected.length} vybráno`;

  const openPanel = useCallback(() => {
    if (ref.current) {
      const r = ref.current.getBoundingClientRect();
      const w = Math.max(220, r.width);
      const left = Math.min(r.left, window.innerWidth - w - 8);
      const below = r.bottom + 4;
      const flipUp = below + 320 > window.innerHeight && r.top - 320 > 0;
      setPos({ left: Math.max(8, left), top: flipUp ? r.top - 324 : below, width: w });
    }
    setOpen(true);
  }, []);

  const toggle = (v) => {
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  };

  return (
    <>
      <button
        type="button"
        ref={ref}
        title={title}
        onClick={() => (open ? setOpen(false) : openPanel())}
        className={`w-full flex min-h-[36px] items-center gap-1 px-2.5 py-1.5 text-xs rounded-xl cursor-pointer transition-all ${
          active
            ? "bg-brand-50 text-brand-deep border-2 border-brand font-bold"
            : "bg-white text-slate-600 border border-slate-200 font-semibold hover:border-slate-300"
        }`}
      >
        <span className="truncate flex-1 text-left">{label}</span>
        <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && pos && createPortal(
        <>
          <div className="fixed inset-0 z-[90]" onClick={() => setOpen(false)} />
          <div
            className="fixed z-[91] bg-white rounded-xl shadow-2xl border border-slate-200 py-1.5 max-h-[320px] overflow-y-auto"
            style={{ left: pos.left, top: pos.top, width: pos.width }}
          >
            {/* Vše = vymazat výběr */}
            <button
              type="button"
              onClick={() => { onChange([]); }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-slate-50 ${!active ? "font-bold text-brand-deep" : "text-slate-600"}`}
            >
              <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${!active ? "bg-brand border-brand" : "border-slate-300"}`}>
                {!active && <Check className="w-3 h-3 text-white" />}
              </span>
              Vše
            </button>
            <div className="h-px bg-slate-100 my-1" />
            {options.map((o) => {
              const checked = selected.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => toggle(o.value)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-slate-50 ${checked ? "font-semibold text-brand-deep" : "text-slate-700"}`}
                >
                  <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${checked ? "bg-brand border-brand" : "border-slate-300"}`}>
                    {checked && <Check className="w-3 h-3 text-white" />}
                  </span>
                  <span className="truncate">{o.label}</span>
                </button>
              );
            })}
          </div>
        </>,
        document.body
      )}
    </>
  );
}
